import { gql } from "@apollo/client"

export const getMsr = gql`
  query Msr {
    me {
      id
      firstName
      msrStatus {
        cycle {
          endDate
        }
        credits {
          label
          earned
          required
          recovery
          totalDeficit
          new
        }
      }
    }
  }
`

export const GET_PDR_REQUIREMENTS_QUERY = gql`
  query getPdrRequirements {
    currentPerson {
      id
      firstName
      pdrRequirements {
        cycleCode
        cycleEndsOn
        cycleStartsOn
        requirementsEffectiveDate
        cohort
        cohortCode
        status
        requirements {
          label
          totalRequirement
          cycleRequirement
          earned
          deficit
          recoveryThreshold
          recoveryRemaining
        }
      }
    }
  }
`

// Kept out of GET_PDR_REQUIREMENTS_QUERY: a backend without this field rejects
// the whole document, and the PDR widget must still load there.
export const GET_PDR_REQUIREMENT_NOTICE_QUERY = gql`
  query getPdrRequirementNotice {
    currentPerson {
      id
      pdrRequirementNotice {
        state
        message
      }
    }
  }
`

export const JOB_PREFERENCES_QUERY = gql`
  query getMeJobPreferenceQuery {
    jobPreferenceFields {
      jobSearchStatus {
        label
        value
      }
      locations {
        label
        value
      }
    }
    jobCategories {
      label
      value
      subCategories {
        label
        value
        type
      }
    }

    me {
      id
      canSeePgaProJob
      jobPreference {
        id
        jobSearchStatus
        jobCategories

        minimumCompensation
        jobLocations
        openToSeasonal
        openToInternational
        optedInToJobSeekerEmails
      }
    }
  }
`

const addressFields = `
  address {
    address1
    address2
    address3
    address4
    zip
    country
    state
    city
  }
`

export const getMeIdQuery = gql`
  query getMeId {
    me {
      id
    }
  }
`

export const getMember = gql`
  query ProfileBriefing {
    me {
      id
      firstName
      lastName
      displayName
      class
      certified
      masterProfessional
      specialized
      photo
      type
      phoneNumber
      primaryEmail
      ${addressFields}
      section {
        id
        name
        website
      }
      primaryFacility {
        id
        name
        ${addressFields}
      }
    }
  }
`
export const EMAIL_MIGRATION_QUERY = gql`
  query EMAIL_MIGRATION_QUERY {
    meEmailMigrationData {
      id
      emailAddress
      tempPassword
      accessDate
      doNotShowAgain
    }
  }
`
export const EMAIL_MIGRATION_MUTATION = gql`
  mutation updateEmailMigration($id: ID!, $input: UpdateEmailMigrationInput) {
    updateEmailMigration(id: $id, input: $input) {
      id
      emailAddress
      tempPassword
      accessDate
      doNotShowAgain
    }
  }
`
export const USER_QUERY = gql`
  query USER_QUERY {
    me {
      id
      firstName
      lastName
      displayName
      class
      certified
      masterProfessional
      specialized
      photo
      type
      role
      phoneNumber
      primaryEmail
      electionDate
      loginEmail
      reciprocityEligible
      ${addressFields}
      section {
        id
        name
        website
      }
      termination {
        status
        type
        terminateCd
        terminateDate
      }
    }
  }
`

export const MEMBERSHIP_EDUCATION_TRAITS_QUERY = gql`
  query MembershipEducationTraits {
    me {
      id
      educationMostRecentCompletedLevel
      educationPgmProgramHistory {
        levelCode
      }
    }
  }
`

const TerminationFragment = gql`
  fragment Termination on MembershipMemberTermination {
    type
    status
    terminateCd
    terminateDate
  }
`

const ApplicationStatusFragment = gql`
  fragment ApplicationStatus on MembershipApplicationStatus {
    applicationType
    status
    startDate
    endDate
  }
`

const ProspectBackgroundCheckFragment = gql`
  fragment ProspectBackgroundCheck on MembershipProspectBackgroundCheck {
    status
    createdAt
    expiredAt
    screenedAt
    updatedAt
  }
`

const ProspectPlayingAbilityTestFragment = gql`
  fragment ProspectPlayingAbilityTest on MembershipProspectPlayingAbilityTest {
    status
    score
    testDate
    expiredAt
    targetScore
  }
`

const ProspectPATStatusFragment = gql`
  fragment ProspectPATStatus on MembershipProspectPATStatus {
    status
    startDate
    expirationDate
  }
`

const ProspectFragment = gql`
  fragment Prospect on MembershipProspect {
    backgroundCheck {
      ...ProspectBackgroundCheck
    }
    backgroundCheckOrder {
      awaitingModeration
      url
      paidAt
      status
    }
    playingAbilityTest {
      ...ProspectPlayingAbilityTest
    }
    playingAbilityTestStatus {
      ...ProspectPATStatus
    }
    qualifyingLevel {
      status
      materialsPurchased
      startDate
      expiredAt
      completedAt
    }
  }
  ${ProspectBackgroundCheckFragment}
  ${ProspectPlayingAbilityTestFragment}
  ${ProspectPATStatusFragment}
`

// Query data from Membership::PersonType
export const MEMBERSHIP_JOURNEY_QUERY = gql`
  query MembershipJourneyQuery {
    me {
      id
      pgaId
      firstName
      lastName
      email
      profilePhoto
      classification
      classificationCode
      committeeCodes
      reciprocityEligible
      gptpSuspension
      suspended
      mainProgramType
      mainProgramTypeCode
      pendingAssociate
      pendingAffiliate
      registeredAffiliate
      primarySection {
        id
        name
      }
      termination {
        ...Termination
      }
      educationLastRequirementCompletedDate
      educationCurrentLevel
      educationMostRecentCompletedLevel
      educationFinalExamStatus
      currentApplicationStatus {
        ...ApplicationStatus
      }
      associateApplicationStatus {
        ...ApplicationStatus
      }
      memberApplicationStatus {
        ...ApplicationStatus
      }
      affiliateApplicationStatus {
        ...ApplicationStatus
      }
      prospect {
        ...Prospect
      }
      pgmProgressStatus {
        currentLevelKey
        currentLevelCode
        mostRecentCompletedLevelKey
        suspensions {
          code
          startDate
          endDate
          note
        }
        curriculumVersion
        programType
        careerPath
        programExpirationDate
        patStatus
        levels {
          key
          numeral
          name
          levelCode
          status
          progressPercent
          startDate
          completionDate
          expirationDate
          requirements {
            key
            name
            requireId
            status
            completionDate
            courseName
            seminarInfo {
              status
              eventId
              eventName
              eventLocation
              eventStartDate
              eventEndDate
              registrationDate
            }
          }
        }
      }
    }
  }
  ${TerminationFragment}
  ${ApplicationStatusFragment}
  ${ProspectFragment}
`

export const PGM_LEVEL_QUERY = gql`
  query PgmLevel {
    me {
      id
      mainProgramTypeCode
      classificationCode
      pgmProgressStatus {
        currentLevelKey
        mostRecentCompletedLevelKey
        curriculumVersion
        careerPath
        levels {
          key
          requirements {
            key
            status
          }
        }
      }
    }
  }
`

export const getMySection = gql`
  query Section {
    me {
      id
      mainProgramTypeCode
      termination {
        type
      }
      primarySection {
        id
        name
        careerConsultants {
          id
          name
          phone
          photo
          email
        }
      }
    }
  }
`

export const getMsrHistory = gql`
  query MsrHistory {
    me {
      id
      msrHistory {
        addedDate
        creditsAccepted
        description1
      }
    }
  }
`

export const getDues = gql`
  query Dues {
    me {
      id
      type
      dues {
        duesBalance
        dueDate
        invoiceDate
        invoiceNum
      }
      termination {
        status
        type
      }
    }
  }
`
export const getMemberForEdit = gql`
  query GetMemberForEdit($id: ID!) {
    member(id: $id) {
      id
      universalId
      firstName
      lastName
      class
      memberClassDescription
      type
      informalName
      displayName
      photo
      certified
      masterProfessional
      address {
        address1
        address2
        address3
        address4
        city
        country
        state
        zip
      }
      facilities {
        id
        name
        section {
          id
          name
        }
        address {
          state
        }
      }
      section {
        id
        name
        address {
          address1
          address2
          address3
          address4
          city
          state
          zip
        }
        primaryFacility {
          phoneNumber
          geolocation {
            lat
            lng
          }
        }
      }
      phoneNumber
      primaryMobile
      publicPhone
      publicMobile
      role
      primaryEmail
      publicEmail
      primaryFacility {
        id
        name
        phoneNumber
        geolocation {
          lat
          lng
        }
        address {
          address1
          address2
          address3
          address4
          city
          country
          state
          zip
        }
      }
      expertise {
        id
        name
      }
      social {
        facebook
        linkedin
        twitter
        youtube
        instagram
        website
      }
      personalCertifications {
        description
        effectiveYear
      }
      officialCertifications {
        description
        effectiveYear
      }
      personalAwards {
        description
        effectiveYear
      }
      officialAwards {
        description
        effectiveYear
      }
      personalAffiliations {
        description
        effectiveYear
      }
      officialAffiliations {
        description
        effectiveYear
      }
      overview
      viewingPermissions {
        viewCertificationsMember
        viewCertificationsPublic
        viewPhonePublic
        viewPhoneMember
        viewAddressPublic
        viewAddressMember
        viewExpertisePublic
        viewExpertiseMember
        viewEmailPublic
        viewEmailMember
        viewMobilePublic
        viewMobileMember
        viewAffiliationsPublic
        viewAffiliationsMember
        viewAwardsPublic
        viewAwardsMember
      }
    }
  }
`
